import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PaginationParams } from '../../../../shared/types/api';
import type { ArticleContentAnalysisResponse, ContentTranscriptResponse } from '../../../../shared/types/content-api';
import { apiClient } from '@/services/api/client';
import { queryKeys } from '@/services/query/query-client';
import { ContentAPIClient } from '@/services/api/content-api';
import { config } from '@/utils/env';
import { getAuthToken } from '@/services/auth/token-manager';
import { useViewContext } from '@/hooks/useViewContext';
import { portalFetch } from './portal/shared';

// ============================================================================
// Unified Content API Client Instance (Option A - Phase 2B)
// ============================================================================

const contentApiClient = new ContentAPIClient(config.apiUrl, getAuthToken);

export type ContentType = 'article' | 'video' | 'instagram_reel' | 'instagram_post' | 'instagram_carousel' | 'tiktok_video' | 'tiktok_slideshow';
export type Platform = 'youtube' | 'instagram' | 'tiktok';

export interface ContentListParams extends PaginationParams {
  content_type?: ContentType;
  content_types?: ContentType[];
  platform?: Platform;
  content_domain_id?: number;
  publisher_id?: number;
  status?: string; // pending | processing | completed | failed
  search?: string;
  start_date?: string;
  end_date?: string;
  order_by?: 'created_at' | 'published_at' | 'title' | 'updated_at' | 'duration_seconds';
  order?: 'asc' | 'desc';
  source_playlist_id?: number; // Filter by source YouTube playlist (videos only)
  /** Narrow to a single ingestion source (input_sources.id). */
  source_id?: string;
}

export interface ContentItem {
  content_id: string;
  slug: string; // Phase 1: Human-readable slug for SEO-friendly URLs
  content_type: ContentType;
  content_domain_id?: number | null;
  title: string;
  url: string;
  published_at?: string | null;
  publisher_id?: number | null;
  publisher_name?: string | null;
  source_platform?: string | null;
  source_name?: string | null;
  processing_status: string;
  created_at?: string;
  updated_at?: string;
  article_id?: number | null;
  video_id?: string | null; // stringified BIGINT
  // Optional fields for backwards compatibility with legacy article/video APIs
  summary?: string | null;
  image_url?: string | null;
  youtube_video_id?: string | null;
  status?: string | null;
  analysis_status?: string | null;
  thumbnail_url?: string | null;
  video_platform_video_id?: string | null;
  video_metadata?: Record<string, unknown> | null;
  domain_name?: string | null;
}

export function useContent(params: ContentListParams = {}, options: { enabled?: boolean; refetchInterval?: number } = {}) {
  const { enabled = true, refetchInterval } = options;
  const actualLimit = params.limit || 20;

  // When using page-based pagination, don't inflate the limit
  // The backend handles offset calculation, so inflating breaks pagination
  const requestLimit = actualLimit;

  return useQuery({
    queryKey: queryKeys.contentList(params),
    queryFn: () => apiClient.getContent({ ...params, limit: requestLimit }),
    enabled,
    refetchInterval,
    select: (data: ContentItem[]) => {
      const page = params.page || 1;
      const offset = params.offset || ((page - 1) * actualLimit);

      // With page-based pagination, we can't use limit+1 to detect hasNext
      // Instead, check if we got exactly the limit (might have more) or less (definitely no more)
      const hasNext = data.length === actualLimit;
      const items = data;

      return {
        items,
        pagination: {
          total: hasNext ? (page * actualLimit) + 1 : (page - 1) * actualLimit + items.length,
          page,
          limit: actualLimit,
          offset,
          hasNext,
          hasPrevious: page > 1,
        }
      };
    },
  });
}

export function useContentDetail(contentId: string, options: { enabled?: boolean } = {}) {
  const { enabled = true } = options;
  return useQuery({
    queryKey: [...queryKeys.content, contentId],
    queryFn: () => apiClient.getContentDetail(contentId),
    enabled: enabled && !!contentId,
    staleTime: 10 * 60 * 1000,
  });
}

export function useContentAnalysis(contentId: string, options: { enabled?: boolean } = {}) {
  const { enabled = true } = options;
  return useQuery({
    queryKey: [...queryKeys.content, contentId, 'analysis'],
    queryFn: () => apiClient.getContentAnalysis(contentId),
    enabled: enabled && !!contentId,
    staleTime: 10 * 60 * 1000,
  });
}

export function useContentProgress(contentId: string, options: { enabled?: boolean; refetchInterval?: number } = {}) {
  const { enabled = true, refetchInterval = 5000 } = options;
  return useQuery({
    queryKey: [...queryKeys.content, contentId, 'progress'],
    queryFn: () => apiClient.getContentProgress(contentId),
    enabled: enabled && !!contentId,
    refetchInterval,
    staleTime: 2000,
  });
}

// ============================================================================
// Unified Content API Hooks (Option A - Phase 2B)
// ============================================================================

/**
 * Hook to fetch video transcript by content UUID or slug
 *
 * @example
 * const { data: transcript, isLoading } = useContentTranscript('bitcoin-halving-explained-video-44');
 */
export function useContentTranscript(identifier: string, options: { enabled?: boolean } = {}) {
  const { enabled = true } = options;
  return useQuery({
    queryKey: [...queryKeys.content, identifier, 'transcript'],
    queryFn: () => contentApiClient.getContentTranscript(identifier),
    enabled: enabled && !!identifier,
    staleTime: 10 * 60 * 1000, // 10 minutes - transcripts don't change
  });
}

/**
 * Mutation hook to translate a non-English transcript to English (CUR-309)
 *
 * Invalidates the transcript cache on success so the UI picks up the translation.
 */
export function useTranslateTranscript(identifier: string) {
  const queryClient = useQueryClient();
  const { isPortalView } = useViewContext();
  return useMutation({
    // Portal users lack the domain permissions the /api route checks, so the
    // portal has its own org-scoped route over the same cached translation.
    mutationFn: () =>
      isPortalView
        ? portalFetch<ContentTranscriptResponse>(`/portal/content/${identifier}/transcript/translate`, { method: 'POST' })
        : contentApiClient.translateTranscript(identifier),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...queryKeys.content, identifier, 'transcript'] });
    },
  });
}

/**
 * Hook to fetch video outline by content UUID or slug
 *
 * Returns comprehensive analysis including Phase 1, 2A, 2B, and 2C fields.
 *
 * @example
 * const { data: outline, isLoading } = useContentOutline('bitcoin-halving-explained-video-44');
 */
export function useContentOutline(identifier: string, options: { enabled?: boolean } = {}) {
  const { enabled = true } = options;
  return useQuery({
    queryKey: [...queryKeys.content, identifier, 'outline'],
    queryFn: () => contentApiClient.getContentOutline(identifier),
    enabled: enabled && !!identifier,
    staleTime: 10 * 60 * 1000, // 10 minutes - outlines don't change often
  });
}

/**
 * Hook to fetch video entities by content UUID or slug
 *
 * @example
 * const { data, isLoading } = useContentEntities('bitcoin-halving-explained-video-44');
 */
export function useContentEntities(identifier: string, options: { enabled?: boolean } = {}) {
  const { enabled = true } = options;
  return useQuery({
    queryKey: [...queryKeys.content, identifier, 'entities'],
    queryFn: () => contentApiClient.getContentEntities(identifier),
    enabled: enabled && !!identifier,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Hook to fetch video relationships by content UUID or slug
 *
 * @example
 * const { data, isLoading } = useContentRelationships('bitcoin-halving-explained-video-44');
 */
export function useContentRelationships(identifier: string, options: { enabled?: boolean } = {}) {
  const { enabled = true } = options;
  return useQuery({
    queryKey: [...queryKeys.content, identifier, 'relationships'],
    queryFn: () => contentApiClient.getContentRelationships(identifier),
    enabled: enabled && !!identifier,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Hook to fetch comprehensive video analysis by content UUID or slug
 *
 * Returns all video data in a single response:
 * - Transcript
 * - Outline (with all Phase 1/2A/2B/2C fields)
 * - Entities
 * - Relationships
 *
 * @example
 * const { data: analysis, isLoading } = useContentAnalysisUnified('bitcoin-halving-explained-video-44');
 */
export function useContentAnalysisUnified(identifier: string, options: { enabled?: boolean } = {}) {
  const { enabled = true } = options;
  return useQuery({
    queryKey: [...queryKeys.content, identifier, 'analysis-unified'],
    queryFn: () => contentApiClient.getContentAnalysis(identifier),
    enabled: enabled && !!identifier,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}


/**
 * Hook to fetch article analysis via unified content endpoint
 *
 * This is used for article detail pages under the /content/article/* routes.
 */
export function useContentArticleAnalysis(identifier: string, options: { enabled?: boolean } = {}) {
  const { enabled = true } = options;
  return useQuery<ArticleContentAnalysisResponse>({
    queryKey: [...queryKeys.content, identifier, 'analysis-article'],
    queryFn: async () =>
      (await contentApiClient.getContentAnalysis(identifier)) as unknown as ArticleContentAnalysisResponse,
    enabled: enabled && !!identifier,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}
